import { FC, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ColumnDef } from '@tanstack/react-table';
import DataTable from '@/components/functional/DataTable/DataTable';
import SelectInput from '@/components/functional/SelectInput/SelectInput';
import { useAdminGradingPrompts, GradingPromptWithCategory } from '@/api/useAdminGradingPrompts';
import Routes from '@/router/Routes';

const GradingPromptsPage: FC = () => {
  const navigate = useNavigate();
  const { data: prompts, isLoading } = useAdminGradingPrompts();

  const [subjectFilter, setSubjectFilter] = useState<string>('');
  const [paperFilter, setPaperFilter] = useState<string>('');

  const filteredPrompts = useMemo(() => {
    if (!prompts) return [];

    return prompts.filter((prompt) => {
      if (subjectFilter && prompt.question_categories.subject !== subjectFilter) {
        return false;
      }
      if (paperFilter && prompt.question_categories.paper_number !== paperFilter) {
        return false;
      }
      return true;
    });
  }, [prompts, subjectFilter, paperFilter]);

  const subjectOptions = useMemo(() => {
    if (!prompts) return [{ value: '', label: 'All Subjects' }];
    const uniqueSubjects = [...new Set(prompts.map((p) => p.question_categories.subject))];
    return [
      { value: '', label: 'All Subjects' },
      ...uniqueSubjects.sort().map((s) => ({ value: s, label: s })),
    ];
  }, [prompts]);

  const paperOptions = useMemo(() => {
    if (!prompts) return [{ value: '', label: 'All Papers' }];
    const uniquePapers = [...new Set(prompts.map((p) => p.question_categories.paper_number))];
    return [
      { value: '', label: 'All Papers' },
      ...uniquePapers.sort().map((p) => ({ value: p, label: p })),
    ];
  }, [prompts]);

  const columns: ColumnDef<GradingPromptWithCategory>[] = useMemo(
    () => [
      {
        accessorKey: 'question_categories.subject',
        header: 'Subject',
        enableSorting: false,
        cell: ({ row }) => (
          <span className="text-gray-900">{row.original.question_categories.subject}</span>
        ),
      },
      {
        accessorKey: 'question_categories.paper_number',
        header: 'Paper',
        enableSorting: false,
        cell: ({ row }) => (
          <span className="text-gray-600">{row.original.question_categories.paper_number}</span>
        ),
      },
      {
        accessorKey: 'category_code',
        header: 'Category Code',
        enableSorting: false,
        cell: (info) => (
          <span className="text-gray-900 font-mono font-medium">{info.getValue() as string}</span>
        ),
      },
      {
        accessorKey: 'question_categories.name',
        header: 'Category Name',
        enableSorting: false,
        cell: ({ row }) => (
          <span className="text-gray-900">{row.original.question_categories.name}</span>
        ),
      },
      {
        id: 'actions',
        header: 'Actions',
        enableSorting: false,
        cell: ({ row }) => (
          <button
            onClick={() =>
              navigate(Routes.ADMIN_EDIT_GRADING_PROMPT.replace(':promptId', row.original.id))
            }
            className="text-[#C87941] font-semibold hover:text-[#A66835] transition-colors"
          >
            Edit
          </button>
        ),
      },
    ],
    [navigate]
  );

  return (
    <div className="min-h-full">
      {/* Header */}
      <div className="bg-white rounded-3xl flex justify-between items-center px-8 py-6">
        <h1 className="text-3xl font-semibold text-gray-900">Grading Prompts</h1>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-3xl mt-6 px-8 py-6">
        <div className="flex gap-4">
          <SelectInput
            label="Subject"
            value={subjectFilter}
            onChange={setSubjectFilter}
            options={subjectOptions}
            wrapperClassName="w-48"
          />
          <SelectInput
            label="Paper"
            value={paperFilter}
            onChange={setPaperFilter}
            options={paperOptions}
            wrapperClassName="w-48"
          />
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-3xl overflow-hidden shadow-sm mt-6">
        <DataTable
          data={filteredPrompts}
          columns={columns}
          wrapperClassName="bg-white"
          headClassName="bg-gray-50 border-t border-gray-100"
          rowClassName="hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
          showPagination={false}
          loading={isLoading}
        />
      </div>

      {/* Template Variables Reference */}
      <div className="bg-white rounded-3xl mt-6 px-8 py-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Template Variables Reference</h2>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <code className="bg-gray-100 px-2 py-1 rounded text-gray-800">{'{{total_marks}}'}</code>
            <span className="ml-2 text-gray-600">Total marks for the question</span>
          </div>
          <div>
            <code className="bg-gray-100 px-2 py-1 rounded text-gray-800">{'{{assessment_objectives}}'}</code>
            <span className="ml-2 text-gray-600">AOs being assessed (e.g., "AO1, AO2")</span>
          </div>
          <div>
            <code className="bg-gray-100 px-2 py-1 rounded text-gray-800">{'{{levels}}'}</code>
            <span className="ml-2 text-gray-600">Formatted mark scheme levels</span>
          </div>
          <div>
            <code className="bg-gray-100 px-2 py-1 rounded text-gray-800">{'{{indicative_content}}'}</code>
            <span className="ml-2 text-gray-600">Example content from mark scheme</span>
          </div>
          <div>
            <code className="bg-gray-100 px-2 py-1 rounded text-gray-800">{'{{special_instructions}}'}</code>
            <span className="ml-2 text-gray-600">Special marking instructions</span>
          </div>
          <div>
            <code className="bg-gray-100 px-2 py-1 rounded text-gray-800">{'{{question_content}}'}</code>
            <span className="ml-2 text-gray-600">The actual question text</span>
          </div>
          <div>
            <code className="bg-gray-100 px-2 py-1 rounded text-gray-800">{'{{mark_scheme_json}}'}</code>
            <span className="ml-2 text-gray-600">Full mark scheme as JSON</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GradingPromptsPage;
